import { Box, IconButton, Autocomplete as MuiAutocomplete } from '@mui/material';
import { CSSProperties, useRef, useState } from 'react';
import { Text, TextInput } from '..';
import { CheckSuccessIcon, EnterGray400Icon, SearchGray500Icon, XGray300Icon } from '@assets/iconComponents';
import { colorSet } from '@theme';

interface ISearchInputProps {
  label?: string;
  boxstyle?: CSSProperties;
  placeholder?: string;
  disabled?: boolean;
  value: string | null;
  options: string[];
  onKeyDown?: (e: React.KeyboardEvent<HTMLDivElement>) => void;
  onInputChange: (event: React.SyntheticEvent, newValue: string) => void;
  onAutocompleteChange?: (event: React.SyntheticEvent, value: string | null) => void;
  onComicNameUpdate?: (value: string) => void;
  onClear?: () => void;
  onSubmit?: () => void;
  isError?: boolean;
  showOneSubIcon?: boolean; // endAdornment에 서브아이콘(엔터 또는 체크) 보여줄지 여부
  showTwoSubIcons?: boolean; // endAdornment에 서브아이콘(X와 돋보기) 보여줄지 여부
}

// 피그마 'Text input' 컴포넌트 + 오토컴플리트 기능 제공
export const SearchInput = (props: ISearchInputProps) => {
  const {
    label,
    boxstyle,
    placeholder,
    disabled = false,
    value,
    options,
    onKeyDown,
    onInputChange,
    onAutocompleteChange,
    onComicNameUpdate,
    onClear,
    onSubmit,
    isError = false,
    showOneSubIcon = false,
    showTwoSubIcons = false
  } = props;

  const [openOptions, setOpenOptions] = useState<boolean>(false);
  const [showSuccessIcon, setShowSuccessIcon] = useState<boolean>(false);
  const [showEnterIcon, setShowEnterIcon] = useState<boolean>(false);

  const isNavigatingWithArrow = useRef<boolean>(false); // 화살표 키로 options를 탐색 중인지 추적

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    // ArrowDown/ArrowUp 키로 옵션 탐색 중임을 표시
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      isNavigatingWithArrow.current = true;
    }

    // Enter 키를 눌렀을 때
    if (e.key === 'Enter') {
      // 화살표 키로 탐색 중이면 Autocomplete의 기본 동작 허용 (onChange 호출)
      if (isNavigatingWithArrow.current && openOptions && options.length > 0) {
        isNavigatingWithArrow.current = false;
        // handleKeyDown prop을 호출하지 않고 Autocomplete의 onChange가 실행되도록 함
        return;
      }

      // 그 외의 경우 (직접 입력한 키워드 + Enter)
      // handleKeyDown prop 호출
      if (onKeyDown) {
        onKeyDown(e);
      }

      const inputValue = (e.target as HTMLInputElement).value;
      if (inputValue?.trim().length) {
        setShowSuccessIcon(true);
        setOpenOptions(false);
      }
    } else {
      // Enter가 아닌 다른 키 입력 시 handleKeyDown prop 호출
      if (onKeyDown) {
        onKeyDown(e);
      }
    }
  };

  const handleFocus = () => {
    setOpenOptions(true);
    setShowEnterIcon(true);
  };

  const handleBlur = () => {
    setOpenOptions(false);
    setShowEnterIcon(false);

    if (value?.trim().length) {
      setShowSuccessIcon(true);
    }
    if (onComicNameUpdate) {
      onComicNameUpdate(value ?? '');
    }
  };

  const handleInputChange = (event: React.SyntheticEvent, newValue: string) => {
    onInputChange(event, newValue);
    setShowSuccessIcon(false);

    if (newValue.length === 0) {
      if (openOptions) {
        setOpenOptions(false);
      }
    } else {
      if (!openOptions) {
        setOpenOptions(true);
      }
    }
  };

  // options 텍스트 중 value와 일치하는 부분을 bold 처리
  const highlightMatch = (option: string, inputValue: string) => {
    if (!inputValue.trim()) {
      return <span style={{ color: colorSet.gray500 }}>{option}</span>;
    }

    const regex = new RegExp(`(${inputValue.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = option.split(regex);

    return (
      <span style={{ color: colorSet.gray500 }}>
        {parts.map((part, index) => (regex.test(part) ? <strong key={index}>{part}</strong> : part))}
      </span>
    );
  };

  return (
    <div style={boxstyle}>
      {label ? (
        <Text variant="subtitle" fontWeight="bold" marginBottom="8px">
          {label}
        </Text>
      ) : null}

      <MuiAutocomplete
        className="search-input-autocomplete" // theme.ts MuiAutocomplete style 적용을 위한 className 부여
        disabled={disabled}
        open={openOptions}
        inputValue={value ?? ''}
        options={options}
        onInputChange={handleInputChange}
        value={null} //inputValue와 options 요소가 일치할 시 onChange가 실행되지 않는 이슈 픽스
        onChange={(event: React.SyntheticEvent<Element, Event>, selectedValue: string | null, reason?: string) => {
          // reason이 'selectOption'이면 키보드/마우스로 옵션을 선택한 것
          if (reason === 'selectOption' && selectedValue) {
            // 옵션 선택 완료 - 화살표 키 탐색 상태 리셋
            isNavigatingWithArrow.current = false;

            if (onAutocompleteChange) {
              onAutocompleteChange(event, selectedValue);
            }
            setShowSuccessIcon(true);
            setOpenOptions(false);
          }
        }}
        isOptionEqualToValue={(option, value) => option === value} //inputValue와 options 요소가 일치할 시 onChange가 실행되지 않는 이슈 픽스
        renderOption={(props, option) => {
          const { key, ...restProps } = props;
          return (
            <li key={key} {...restProps}>
              {highlightMatch(option, value ?? '')}
            </li>
          );
        }}
        renderInput={(params) => {
          return (
            <TextInput
              {...params}
              InputProps={{
                ...params.InputProps,
                style: { cursor: 'text' },
                endAdornment: showOneSubIcon ? (
                  <div style={{ paddingLeft: 16 }}>
                    {showSuccessIcon ? <CheckSuccessIcon /> : <>{showEnterIcon ? <EnterGray400Icon /> : null}</>}
                  </div>
                ) : showTwoSubIcons ? (
                  value ? (
                    <>
                      <IconButton
                        onClick={() => {
                          if (onClear) {
                            onClear();
                          }
                        }}
                      >
                        <XGray300Icon style={{ width: 20, height: 20 }} />
                      </IconButton>
                      <Box
                        style={{
                          height: 32,
                          borderRight: `0.5px solid ${colorSet.gray300}`,
                          marginLeft: 10,
                          marginRight: 12
                        }}
                      />
                      <IconButton
                        onClick={() => {
                          if (onSubmit) {
                            onSubmit();
                          }
                          setOpenOptions(false);
                        }}
                        sx={{ cursor: 'default', marginRight: '-4px' }}
                      >
                        <SearchGray500Icon style={{ width: 20, height: 20 }} />
                      </IconButton>
                    </>
                  ) : (
                    <SearchGray500Icon style={{ width: 20, height: 20, marginRight: '-4px' }} />
                  )
                ) : null
              }}
              isError={isError}
              placeholder={placeholder ?? ''}
              onKeyDown={handleKeyDown}
              onFocus={handleFocus}
              onBlur={handleBlur}
            />
          );
        }}
        clearIcon={false} // 값 입력시 보여지는 X 버튼을 보이지 않게 처리
      />
    </div>
  );
};
